import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Box, 
  Typography, 
  IconButton, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Avatar, 
  Divider,
  Paper,
  Drawer,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  Storage as StorageIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

import Dashboard from './components/Dashboard';
import BillGenerator from './components/BillGenerator';
import BillDatabase from './components/BillDatabase';
import CustomerManagement from './components/CustomerManagement';
import CompanySettings from './components/CompanySettings';
import Login from './components/Login';
import { supabase } from './supabaseClient';

const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1', // Indigo primary
      contrastText: '#ffffff'
    },
    background: {
      default: '#f8fafc', // slate-50 background
      paper: '#ffffff'
    },
    text: {
      primary: '#0f172a', // slate-900
      secondary: '#475569' // slate-600
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: 'none',
      fontWeight: '600'
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0
        }
      }
    }
  }
});

const SIDEBAR_WIDTH = 260;

function App() {
  const [currentTab, setCurrentTab] = useState(0); // 0: Dashboard, 1: Generate Bill, 2: Bill Ledger, 3: Customers, 4: Company Settings
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCompany, setActiveCompany] = useState('');
  
  // Clone (Duplicate Bill) State
  const [clonePayload, setClonePayload] = useState(null);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Fetch custom user profile roles from database
  const fetchUserProfile = async (sessionUser) => {
    if (!sessionUser) return null;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username, role')
        .eq('id', sessionUser.id)
        .single();
      
      if (error) throw error;
      return {
        id: sessionUser.id,
        email: sessionUser.email,
        username: data?.username || sessionUser.email,
        role: data?.role || 'user'
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return {
        id: sessionUser.id,
        email: sessionUser.email,
        username: sessionUser.user_metadata?.username || sessionUser.email,
        role: sessionUser.user_metadata?.role || 'user'
      };
    }
  };

  const loadActiveCompany = async () => {
    try {
      const companyId = localStorage.getItem('selectedCompanyId');
      if (companyId) {
        const { data } = await supabase.from('companies').select('name').eq('id', companyId).single();
        if (data) setActiveCompany(data.name);
      } else {
        const { data } = await supabase.from('companies').select('id, name');
        if (data && data.length > 0) {
          localStorage.setItem('selectedCompanyId', data[0].id);
          setActiveCompany(data[0].name);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    // Check active session on initial load
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const profile = await fetchUserProfile(session.user);
        setUser(profile);
        loadActiveCompany();
      }
      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const profile = await fetchUserProfile(session.user);
        setUser(profile);
        loadActiveCompany();
      } else {
        setUser(null);
        setActiveCompany('');
      }
      setLoading(false);
    });

    // Collapse sidebar by default on smaller screens
    if (isMobile) {
      setSidebarOpen(false);
    }

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const handleLogin = (userData) => {
    setUser(userData);
    loadActiveCompany();
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setCurrentTab(0);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleDuplicateBill = (billPayload) => {
    setClonePayload(billPayload);
    setCurrentTab(1); // Redirect to Generate Bill
  };

  const clearClonePayload = () => {
    setClonePayload(null);
  };

  const handleSidebarNavigation = (tabIndex) => {
    setCurrentTab(tabIndex);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Typography fontWeight="600">Loading Bill Automation System...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  // Sidebar Menu List
  const navItems = [
    { label: 'Dashboard', icon: <DashboardIcon /> },
    { label: 'Generate Bill', icon: <ReceiptIcon /> },
    { label: 'Bill Ledger', icon: <StorageIcon /> },
    { label: 'Customers', icon: <PeopleIcon /> },
    { label: 'Company Settings', icon: <SettingsIcon /> }
  ];

  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f172a', color: '#f8fafc' }}>
      {/* Brand Header */}
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #1e293b' }}>
        
        <Typography variant="h4" fontWeight="700" color="#f8fafc">
          BillHub
        </Typography>
      </Box>

      {/* Nav List */}
      <List sx={{ px: 2, py: 3, flexGrow: 1 }}>
        {navItems.map((item, idx) => {
          const isActive = currentTab === idx;
          return (
            <ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleSidebarNavigation(idx)}
                sx={{
                  borderRadius: 2,
                  py: 1.25,
                  px: 2,
                  bgcolor: isActive ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                  color: isActive ? '#818cf8' : '#94a3b8',
                  borderLeft: isActive ? '3px solid #6366f1' : '3px solid transparent',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    color: '#f8fafc',
                    '& .MuiListItemIcon-root': { color: '#f8fafc' }
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40, 
                  color: isActive ? '#818cf8' : '#64748b' 
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive ? '600' : '500' }} 
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: '#1e293b' }} />

      {/* Profile & Footer */}
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {activeCompany && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#1e293b', px: 2, py: 1.25, borderRadius: 2 }}>
            <BusinessIcon sx={{ color: '#818cf8', width: 20, height: 20 }} />
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="caption" color="#64748b" sx={{ display: 'block', fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Active Company
              </Typography>
              <Typography variant="body2" fontWeight="600" color="#f8fafc" noWrap sx={{ fontSize: '0.8rem' }}>
                {activeCompany}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#818cf8', width: 36, height: 36, fontSize: '0.95rem', fontWeight: '600' }}>
            {user.username.substring(0, 2).toUpperCase()}
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight="600" color="#f8fafc" noWrap>
              {user.username}
            </Typography>
            <Typography variant="caption" color="#64748b" sx={{ display: 'block', textTransform: 'capitalize' }}>
              {user.role} role
            </Typography>
          </Box>
        </Box>


        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            py: 1,
            px: 2,
            bgcolor: 'rgba(239, 68, 68, 0.1)',
            color: '#f87171',
            '&:hover': {
              bgcolor: 'rgba(239, 68, 68, 0.2)',
              color: '#ef4444'
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Log out" primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: '600' }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        
        {/* Desktop Sidebar drawer */}
        {!isMobile && (
          <Drawer
            variant="permanent"
            open
            PaperProps={{
              sx: {
                width: SIDEBAR_WIDTH,
                border: 'none',
                boxShadow: '2px 0 12px rgba(15, 23, 42, 0.04)'
              }
            }}
          >
            {sidebarContent}
          </Drawer>
        )}

        {/* Mobile Sidebar drawer */}
        {isMobile && (
          <Drawer
            variant="temporary"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: SIDEBAR_WIDTH,
                border: 'none'
              }
            }}
          >
            {sidebarContent}
          </Drawer>
        )}

        {/* Workspace panel */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          pl: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
          minWidth: 0
        }}>
          {/* Top AppBar Header (Mobile Only) */}
          {isMobile && (
            <Paper 
              elevation={0}
              square
              sx={{ 
                p: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                borderBottom: '1px solid #e2e8f0',
                bgcolor: 'background.paper',
                borderRadius: 0,
                zIndex: 10
              }}
            >
              <IconButton onClick={() => setSidebarOpen(true)} edge="start" sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
              <Typography variant="subtitle1" fontWeight="1000">
                BillHub 
              </Typography>
            </Paper>
          )}

          {/* Main workspace scrollable content */}
          <Box sx={{ p: 4, flexGrow: 1, overflowY: 'auto' }}>
            {currentTab === 0 && <Dashboard onNavigate={handleSidebarNavigation} />}
            {currentTab === 1 && (
              <BillGenerator 
                clonePayload={clonePayload} 
                onClearClone={clearClonePayload} 
              />
            )}
            {currentTab === 2 && <BillDatabase onDuplicateBill={handleDuplicateBill} />}
            {currentTab === 3 && <CustomerManagement />}
            {currentTab === 4 && <CompanySettings onCompanyChange={loadActiveCompany} />}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
